import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
  SearchBox,
  useMobile,
} from '@octano/global-ui';

import { TranslationsKeys } from '../../../../locales/translations';
import { useAuthorizationDataLoader } from './AuthorizationDataLoader';
import { useAuthorizationFilterLoader } from './AuthorizationFilterLoader';

interface FilterFormValues {
  nameOrId: string | null;
  studyPlanId: OutlinedSelectOptionType | null;
  status: OutlinedSelectOptionType | null;
}

const DEFAULT_VALUES: FilterFormValues = {
  nameOrId: null,
  studyPlanId: null,
  status: null,
};

export default function AuthorizationSearchBar() {
  const { t } = useTranslation(TranslationsKeys.HOMOLOGATION_AUTHORIZATION);
  const { updateQuery } = useAuthorizationDataLoader();
  const { data: formData } = useAuthorizationFilterLoader();
  const isMobile = useMobile();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm<FilterFormValues>({ defaultValues: DEFAULT_VALUES });

  const currentValues = watch();

  const studyPlanOptions = useMemo<OutlinedSelectOptionType[]>(
    () =>
      (formData?.studyPlans ?? []).map((sp) => ({
        value: sp.id,
        label: sp.name,
      })),
    [formData],
  );

  const statusOptions = useMemo<OutlinedSelectOptionType[]>(
    () => [
      { value: 'pending_signature', label: t('status.pending_signature') },
      { value: 'approved', label: t('status.approved') },
      { value: 'rejected', label: t('status.rejected') },
    ],
    [t],
  );

  const onSubmit = useCallback(
    (values: FilterFormValues) => {
      updateQuery((prev) => ({
        ...prev,
        search: values.nameOrId || undefined,
        studyPlanId: values.studyPlanId
          ? Number(values.studyPlanId.value)
          : undefined,
        status: values.status ? String(values.status.value) : undefined,
        page: 1,
      }));
    },
    [updateQuery],
  );

  const cleanFilters = useCallback(() => {
    const needToClean = Object.values(currentValues).some(
      (value) => value !== null,
    );
    if (needToClean) {
      reset();
      updateQuery((prev) => ({
        ...prev,
        search: undefined,
        studyPlanId: undefined,
        status: undefined,
        page: 1,
      }));
    }
  }, [currentValues, reset, updateQuery]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row className="pb-4 gutters-filters">
        <Col xs={12} md={3}>
          <SearchBox
            name="nameOrId"
            label={t('filters.nameOrId')}
            placeholder={t('filters.nameOrIdPlaceholder')}
            control={control}
            clear={() => setValue('nameOrId', '')}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="studyPlanId"
            options={studyPlanOptions}
            label={t('filters.program')}
            placeholder={t('filters.programPlaceholder')}
            control={control}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="status"
            options={statusOptions}
            label={t('filters.status')}
            placeholder={t('filters.statusPlaceholder')}
            control={control}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <div
            className="d-flex"
            style={{ marginTop: isMobile ? 0 : '1.5rem', gap: 10 }}
          >
            <Button
              type="submit"
              text={t('filters.search')}
              size="sm"
              style={{
                width: isMobile ? '100%' : 125,
                height: 41,
              }}
            />
            <Button
              text={t('filters.clean')}
              size="sm"
              outlined
              onClick={cleanFilters}
              style={{
                width: isMobile ? '100%' : 125,
                height: 41,
              }}
            />
          </div>
        </Col>
      </Row>
    </Form>
  );
}
